import { ONE_SIGNAL_CATEGORY_INTEREST_STORAGE_KEY } from 'enums/oneSignal'

export const CATEGORY_INTEREST_VISIT_THRESHOLD = 3

function normalizeSlug(slug) {
  return (slug || '').toLowerCase()
}

export function getOneSignalInterestTagKeys(categorySlug, subcategorySlug) {
  const normalizedCategorySlug = normalizeSlug(categorySlug)

  if (!normalizedCategorySlug) return []

  const tagKeys = [normalizedCategorySlug]
  const normalizedSubcategorySlug = normalizeSlug(subcategorySlug)

  if (normalizedSubcategorySlug) {
    tagKeys.push(`${normalizedCategorySlug}/${normalizedSubcategorySlug}`)
  }

  return tagKeys
}

function readCategoryInterestStorage() {
  try {
    const rawValue = localStorage.getItem(
      ONE_SIGNAL_CATEGORY_INTEREST_STORAGE_KEY
    )

    if (!rawValue) return {}

    const parsedValue = JSON.parse(rawValue)

    return parsedValue && typeof parsedValue === 'object' ? parsedValue : {}
  } catch (_) {
    return {}
  }
}

function writeCategoryInterestStorage(data) {
  try {
    localStorage.setItem(
      ONE_SIGNAL_CATEGORY_INTEREST_STORAGE_KEY,
      JSON.stringify(data)
    )
  } catch (_) {
    // Fail silently — tags are for segmentation only
  }
}

function recordCategoryInterestVisit(tagKey, contentItemId) {
  if (!tagKey || contentItemId == null) return 0

  const contentItemIdAsString = String(contentItemId)
  const storage = readCategoryInterestStorage()
  const visitedContentItemIds = Array.isArray(storage[tagKey])
    ? [...storage[tagKey]]
    : []

  if (visitedContentItemIds.includes(contentItemIdAsString)) {
    return visitedContentItemIds.length
  }

  if (visitedContentItemIds.length >= CATEGORY_INTEREST_VISIT_THRESHOLD) {
    return visitedContentItemIds.length
  }

  visitedContentItemIds.push(contentItemIdAsString)
  storage[tagKey] = visitedContentItemIds
  writeCategoryInterestStorage(storage)

  return visitedContentItemIds.length
}

function sendOneSignalTag(oneSignal, tagKey) {
  oneSignal.push(async () => {
    try {
      const [userId, isSubscribed] = await Promise.all([
        oneSignal.getUserId(),
        oneSignal.getSubscription()
      ])

      if (!userId && !isSubscribed) return

      await oneSignal.sendTag(tagKey, tagKey)
    } catch (_) {
      // Fail silently — tags are for segmentation only
    }
  })
}

export function getQualifyingTagKeys() {
  const storage = readCategoryInterestStorage()

  return Object.entries(storage)
    .filter(
      ([, articleIds]) =>
        Array.isArray(articleIds) &&
        articleIds.length >= CATEGORY_INTEREST_VISIT_THRESHOLD
    )
    .map(([tagKey]) => tagKey)
}

export function syncCategoryInterestTagsFromStorage() {
  const tagKeys = getQualifyingTagKeys()

  if (!tagKeys.length || typeof window === 'undefined' || !window.OneSignal) {
    return
  }

  const oneSignal = window.OneSignal
  const tags = Object.fromEntries(tagKeys.map(tagKey => [tagKey, tagKey]))

  oneSignal.push(async () => {
    try {
      const [userId, isSubscribed] = await Promise.all([
        oneSignal.getUserId(),
        oneSignal.getSubscription()
      ])

      if (!userId && !isSubscribed) return

      await oneSignal.sendTags(tags)
    } catch (_) {
      // Fail silently — tags are for segmentation only
    }
  })
}

export function addOneSignalInterestTag({
  categorySlug,
  subcategorySlug,
  contentItemId
}) {
  if (typeof window === 'undefined') return

  const tagKeys = getOneSignalInterestTagKeys(categorySlug, subcategorySlug)

  if (!tagKeys.length) return

  const oneSignal = window.OneSignal

  tagKeys.forEach(tagKey => {
    const visitCount = recordCategoryInterestVisit(tagKey, contentItemId)

    if (visitCount < CATEGORY_INTEREST_VISIT_THRESHOLD) return
    if (!oneSignal) return

    sendOneSignalTag(oneSignal, tagKey)
  })
}
